import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { logEvent } from '~/config/firebase.config';
import { UserDatabase } from '~/types/user';
import { getAdventurerURL } from '~/config/endpoints.config';
import { useAuth } from '~/services/auth';
import { trackMetaPixel } from '~/utils/attribution';
import { getSignupFlow } from '~/utils/signupFlow';
import { formatDayOffset } from '~/utils/trialPricing';

import { useAccess } from './hooks/useAccess';
import { errorMessage } from './utils';
import { AuthTimeline } from './components/AuthTimeline';
import { AuthShell, AuthField, AuthSubmit, AuthGoogle, AuthSeparator, AuthLink, AuthLegalNotice } from './AuthKit';
import styles from './auth.redesign.module.css';

/**
 * Sign up page.
 */
export function SignUp({ onSignup, onSuccessSignUp }: any) {
    const { t, i18n } = useTranslation();
    // Test A/B du parcours (utils/signupFlow) : tiré une fois par navigateur.
    const [flow] = useState(() => getSignupFlow());
    const v2 = flow === 'v2';

    const { accessSignIn } = useAccess();

    const { loading, signup, loginWithGoogle, resetError, emailErrorCode, passwordErrorCode } = useAuth();

    const resetFields = useAuth((state) => state.resetFields);

    useEffect(() => {
        logEvent('signup_view', 'SignUp', { flow });
        // Événement par version : comptable dans GA4 sans dimension personnalisée.
        logEvent(`signup_view_${flow}`, 'SignUp');
        document.title = 'Sign Up - Unboared';

        // Persist coupon code from URL (e.g. ?coupon=CINEO) for checkout
        const params = new URLSearchParams(window.location.search);
        const coupon = params.get('coupon');
        if (coupon) {
            localStorage.setItem('signup_coupon', coupon);
        }
        // Formule choisie sur le site (?plan=annual|pro|pro_annual, ou ?plan=annual&from=pro pour le
        // Pro à l'année) : pré-sélectionnée à l'étape Infos (l'adresse y perd ses paramètres).
        // Une inscription sans ?plan repart du Standard au mois (pas de choix d'une visite précédente).
        try {
            let plan = params.get('plan');
            if (plan === 'annual' && params.get('from') === 'pro') plan = 'pro_annual';
            if (plan && ['annual', 'pro', 'pro_annual'].includes(plan)) localStorage.setItem('signup_plan', plan);
            else localStorage.removeItem('signup_plan');
        } catch {
            /* stockage indisponible (navigation privée) : mensuel par défaut */
        }
        // (UTM + fbclid : capturés au bootstrap dans main.tsx)
    }, []);

    const login = () => {
        logEvent('link_signin_click', 'SignUp');
        resetFields();
        if (onSignup) {
            onSignup();
        } else {
            accessSignIn();
        }
    };

    const handleSubmit = (e: any) => {
        logEvent('signup_click', 'SignUp');
        e.preventDefault();
        const formData = new FormData(e.target);
        const email = formData.get('email') as string;
        const password = formData.get('password') as string;
        // v2 : pas de confirmation (l'œil permet de vérifier le mot de passe).
        const confirmPassword = v2 ? undefined : (formData.get('confirm-password') as string);
        signup(email, password, confirmPassword)
            .then((auth) => {
                if (auth) {
                    UserDatabase.createUser(
                        auth.user.uid,
                        email,
                        getAdventurerURL(email),
                        '' /* firstname */,
                        '' /* lastname */
                    ).then(() => {
                        logEvent('signup_success', 'SignUp', { flow });
                        logEvent(`signup_done_${flow}`, 'SignUp');
                        trackMetaPixel('Lead');
                        if (onSuccessSignUp) {
                            onSuccessSignUp(auth);
                        }
                    });
                }
            })
            .catch((error) => {
                logEvent('signup_failed', 'SignUp');
                console.error('Error creating user in database:', error);
            });
    };

    const handleGoogleSignUp = () => {
        logEvent('google_signup_click', 'SignUp');
        loginWithGoogle()
            .then((auth) => {
                if (auth) {
                    UserDatabase.createUser(
                        auth.user.uid,
                        auth.user.email || '',
                        getAdventurerURL(auth.user.email || ''),
                        '' /* firstname */,
                        '' /* lastname */
                    ).then(() => {
                        logEvent('google_signup_success', 'SignUp');
                        // Le bouton Google sert aussi de re-login : Lead
                        // uniquement si le compte vient d'être créé
                        const { metadata } = auth.user;
                        if (metadata.creationTime === metadata.lastSignInTime) {
                            trackMetaPixel('Lead');
                            logEvent(`signup_done_${flow}`, 'SignUp');
                        }
                        if (onSuccessSignUp) {
                            onSuccessSignUp(auth);
                        }
                    });
                }
            })
            .catch((error) => {
                logEvent('google_signup_failed', 'SignUp');
                console.error('Error creating user in database:', error);
            });
    };

    return (
        <AuthShell>
            <form onSubmit={handleSubmit}>
                <AuthTimeline currentStep={0} trialLabel={v2} />

                {v2 ? (
                    <>
                        <h2 className={styles.title}>{t('global.signup.v2.title')}</h2>
                        <ul className={styles.promise}>
                            <li>{t('global.signup.v2.today')}</li>
                            <li>{t('global.signup.v2.card', { date: formatDayOffset(14, i18n.language) })}</li>
                            <li>{t('global.signup.v2.cancel')}</li>
                        </ul>
                    </>
                ) : (
                    <>
                        <h2 className={styles.title}>{t('global.signup.title')}</h2>
                        <p className={styles.subtitle}>{t('global.signup.subtitle')}</p>
                    </>
                )}

                <AuthGoogle onClick={handleGoogleSignUp}>{t('global.signup.googleButton')}</AuthGoogle>
                <AuthSeparator label={t('global.or')} />

                <AuthField
                    id="email"
                    label={t('global.signup.input.email')}
                    placeholder={t('global.signup.input.emailPlaceholder')}
                    autoComplete="email"
                    onSelect={resetError}
                    error={emailErrorCode !== undefined}
                    helper={
                        emailErrorCode !== undefined ? t(`global.error.${errorMessage(emailErrorCode)}`) : undefined
                    }
                />
                <AuthField
                    id="password"
                    password
                    label={t('global.signup.input.password')}
                    placeholder={t('global.signup.input.passwordPlaceholder')}
                    autoComplete="new-password"
                    onSelect={resetError}
                    error={passwordErrorCode !== undefined}
                    helper={
                        passwordErrorCode !== undefined
                            ? t(`global.error.${errorMessage(passwordErrorCode)}`)
                            : undefined
                    }
                />
                {!v2 && (
                    <AuthField
                        id="confirm-password"
                        password
                        label={t('global.signup.input.confirmPassword')}
                        placeholder={t('global.signup.input.confirmPasswordPlaceholder')}
                        autoComplete="new-password"
                        onSelect={resetError}
                    />
                )}

                <AuthSubmit loading={loading}>{t(v2 ? 'global.signup.v2.button' : 'global.signup.button')}</AuthSubmit>
                <AuthLegalNotice i18nKey="global.signup.legalNotice" />

                <p className={styles.foot}>
                    {t('global.signup.alreadyAccount')} <AuthLink onClick={login}>{t('global.signup.login')}</AuthLink>
                </p>
            </form>
        </AuthShell>
    );
}
